import React, { useState, useEffect, useRef } from "react";
import {
  OptionsList as StyledOptionsList,
  Option as StyledOption,
  Title,
  Text,
  SubText,
  Checkbox,
} from "./index.styled";
import { ReactComponent as SortByIcon } from "../../Assets/Icons/SortByIcon.svg";

const SearchList = ({
  options = [],
  onOptionClick,
  selectedOption,
  customWidth,
  customPadding,
  customBorderRadius,
  top,
  right,
  showTitle,
  isOpen,
  showIcon,
  showCheckbox,
  height,
}) => {
  const [highlightedIndex, setHighlightedIndex] = useState(0);
  const listRef = useRef(null);

  useEffect(() => {
    if (isOpen) {
      setHighlightedIndex(-1);
    }
  }, [isOpen]);

  useEffect(() => {
    if (highlightedIndex >= 0 && listRef.current) {
      const list = listRef.current;
      const highlightedItem = list.children[highlightedIndex];

      if (highlightedItem) {
        const itemHeight = highlightedItem.offsetHeight;
        const listHeight = list.clientHeight;
        const scrollTop = list.scrollTop;
        const itemTop = highlightedItem.offsetTop;
        const itemBottom = itemTop + itemHeight;

        // Calculate the ideal scroll position to keep item in the center
        const idealScrollTop = itemTop - listHeight / 2 + itemHeight / 2;

        // Prevent scrolling when the first option is selected
        if (
          highlightedIndex === 0 ||
          highlightedIndex === 1 ||
          highlightedIndex === 2
        ) {
          list.scrollTop = 0;
        }
        // Prevent extra scrolling when moving from last to first
        else if (
          highlightedIndex === options.length - 1 &&
          scrollTop > itemTop
        ) {
          list.scrollTop = list.scrollHeight - listHeight;
        }
        // Scroll normally for other items
        else if (itemBottom > listHeight + scrollTop) {
          list.scrollTop = idealScrollTop;
        } else if (itemTop < scrollTop) {
          list.scrollTop = idealScrollTop;
        }
      }
    }
  }, [highlightedIndex]);

  const handleSortClick = (e) => {
    e.stopPropagation();
  };

  const handleClick = (option) => {
    if (option.id) {
      onOptionClick(option.id, "", option.section);
    } else {
      onOptionClick("", option.label, option.section);
    }
  };

  const handleKeyDown = (e) => {
    if (!isOpen) return;

    if (e.key === "ArrowDown") {
      setHighlightedIndex((prev) => (prev < options.length - 1 ? prev + 1 : 0));
    } else if (e.key === "ArrowUp") {
      setHighlightedIndex((prev) => (prev > 0 ? prev - 1 : options.length - 1));
    } else if (e.key === "Enter" && highlightedIndex >= 0) {
      handleClick(options[highlightedIndex]);
    }
  };

  useEffect(() => {
    if (isOpen && listRef.current) {
      setHighlightedIndex(0);
      const firstOption = listRef.current.children[0];
      if (firstOption) {
        firstOption.focus();
      }
    }
  }, [isOpen]);

  useEffect(() => {
    const handleGlobalKeyDown = (e) => {
      if (e.key === "ArrowDown") {
        const firstOption = listRef.current.children[0];
        if (firstOption) {
          firstOption.focus();
        }
      }
    };

    document.addEventListener("keydown", handleGlobalKeyDown);
    return () => document.removeEventListener("keydown", handleGlobalKeyDown);
  }, []);

  return (
    <StyledOptionsList
      ref={listRef}
      customWidth={customWidth}
      customPadding={customPadding}
      top={top}
      right={right}
      isOpen={isOpen}
      height={height}
      tabIndex={0}
      onKeyDown={handleKeyDown}
    >
      {showTitle && (
        <Title onClick={handleSortClick}>
          <Text>{showTitle}</Text>
          {showIcon && <SortByIcon />}
        </Title>
      )}
      {options.length === 0 && (
        <Title>
          <Text>Nothing found...</Text>
        </Title>
      )}
      {options.map((option, index) => (
        <StyledOption
          key={option.value}
          onClick={() => handleClick(option)}
          isSelected={
            showCheckbox
              ? selectedOption?.includes(option.value)
              : selectedOption === option.value
          }
          customBorderRadius={customBorderRadius}
          className={highlightedIndex === index ? "highlighted" : ""}
          tabIndex={-1}
        >
          <div>
            {option.label}
            {option.location && <SubText>{option.location}</SubText>}
          </div>
          {showCheckbox && (
            <Checkbox
              type="checkbox"
              checked={showCheckbox && selectedOption?.includes(option.value)}
              onChange={() => onOptionClick(option)}
              onClick={(e) => e.stopPropagation()}
            />
          )}
        </StyledOption>
      ))}
    </StyledOptionsList>
  );
};

export default SearchList;
